import React, { useEffect, useState, useRef } from 'react';
import { Send, MessageSquare, Loader2, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

export default function Messages() {
  const { profile, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageError, setMessageError] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const fetchMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true });
        
        // Catch table not found error and just show empty state
        if (error && error.code !== '42P01') throw error;
        setMessages(data || []);

        // Mark unread admin messages as read
        const unreadAdminMsgs = (data || []).filter(m => m.is_from_admin && !m.user_has_read);
        if (unreadAdminMsgs.length > 0) {
          await supabase
            .from('messages')
            .update({ user_has_read: true })
            .eq('user_id', user.id)
            .eq('is_from_admin', true)
            .eq('user_has_read', false);
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();

    // In a real app, you would set up a Supabase Realtime subscription here
    // to listen for new messages from the admin.
  }, [user]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    setSending(true);
    setMessageError('');
    const messageText = newMessage.trim();
    setNewMessage(''); // Optimistic UI clear

    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          user_id: user.id,
          message: messageText,
          is_from_admin: false,
        })
        .select()
        .single();

      if (error && error.code !== '42P01') throw error;
      
      // If table doesn't exist yet, just mock it in UI
      const addedMessage = data || {
        id: Date.now(),
        user_id: user.id,
        message: messageText,
        is_from_admin: false,
        created_at: new Date().toISOString()
      };

      setMessages([...messages, addedMessage]);

      // Send push notification to admins
      const senderName = profile ? `${profile.first_name} ${profile.last_name}` : (user.email || 'A client');
      supabase.functions.invoke('send-push', {
        body: {
          target_role: 'admin',
          title: `New Message from ${senderName}`,
          body: messageText,
          url: `/admin/messages`
        }
      }).catch(e => console.error('Error triggering push to admin:', e));
    } catch (err) {
      console.error('Error sending message:', err);
      setMessageError('Failed to send message. Please try again.');
      setNewMessage(messageText); // Restore input on failure
      setTimeout(() => {
        setMessageError(prev => prev === 'Failed to send message. Please try again.' ? '' : prev);
      }, 5000);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Support Messages</h1>
        <p className="mt-1 text-slate-500">Chat directly with your dedicated career coach and admin team.</p>
      </div>

      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col overflow-hidden">
        {/* Chat Header */}
        <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Placement Point Team</h3>
            <p className="text-xs text-slate-500">Usually replies within a few hours</p>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="w-8 h-8 text-slate-400" />
              </div>
              <h4 className="text-lg font-bold text-slate-700 mb-1">Start a Conversation</h4>
              <p className="text-sm text-slate-500 max-w-sm">
                Have a question about your applications, CV, or interview prep? Send us a message!
              </p>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.is_from_admin ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`flex gap-3 max-w-[85%] sm:max-w-[70%] ${msg.is_from_admin ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className="flex-shrink-0 mt-auto">
                      {msg.is_from_admin ? (
                        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                          <MessageSquare className="w-4 h-4 text-indigo-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                          {profile?.avatar_url ? (
                            <img src={profile.avatar_url} alt="You" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-slate-600" />
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className={`px-4 py-3 rounded-2xl ${
                      msg.is_from_admin 
                        ? 'bg-slate-100 text-slate-800 rounded-bl-none' 
                        : 'bg-indigo-600 text-white rounded-br-none'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-[10px] mt-1 text-right ${
                        msg.is_from_admin ? 'text-slate-400' : 'text-indigo-200'
                      }`}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Chat Input */}
        <div className="p-4 border-t border-slate-100 bg-white">
          {messageError && (
            <div className="mb-2 text-rose-600 text-xs font-semibold flex items-center gap-1.5 animate-fadeIn">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{messageError}</span>
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-slate-50"
            />
            <button
              type="submit"
              disabled={sending || !newMessage.trim()}
              className="px-5 py-3 bg-indigo-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              <span className="hidden sm:inline">Send</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
